/**
 * "Before you assign this" (#1431, C3-3).
 *
 * Built to threads/cycle-3-credential-gating/mockup.html, panels 1 and 2. A
 * deviation from the mockup is a bug (docs/bible/design-to-code-handoff.md).
 *
 * WHAT CHANGED, AND IT IS ONLY THE TIMING. Every one of these checks already
 * ran — on the way OUT, as a toast, after the job had landed on the tech. The
 * information arrived after the decision, which makes it a record rather than
 * a warning. This asks first.
 *
 * A WARNING, NEVER A BLOCK. Office override is a documented workflow: every
 * path here ends in "Assign anyway" being available, and the reason the office
 * types lands on office review exactly as it does today. What the office may
 * not do is find out afterwards.
 *
 * A DROP THAT IS FINE JUST LANDS. `askBeforeAssign` resolves immediately with
 * no dialog when the server says the pairing is clean — the dispatcher who is
 * doing nothing wrong never sees a modal.
 */
import { useCallback, useRef, useState } from "react";
import { Dialog } from "@/components/ui/dialog";
import { Button } from "@/components/ui/button";
import { api } from "@/lib/api";

export type EligibilityKind = "approval" | "license" | "gauge" | "customer_type" | "jurisdiction";

export interface EligibilityReason {
    kind: EligibilityKind;
    sentence: string;
    detail?: string;
    whoCan: string[];
    fixHref?: string;
    fixLabel?: string;
}

export interface AssignmentEligibility {
    jobId: string;
    techId: string;
    techName: string;
    jobTypeName: string | null;
    siteName: string | null;
    waterProviderName: string | null;
    eligible: boolean;
    reasons: EligibilityReason[];
}

export interface AssignmentDecision {
    proceed: boolean;
    /** What the office typed in the "say why" box. Rides to office review. */
    reason?: string;
    /** Set when the office chose to pair with a registered tech instead. */
    pairWith?: string;
}

/** The heading that tells the office who else could take it. */
function whoLabel(kind: EligibilityKind, provider: string | null): string {
    if (kind === "jurisdiction") return `Registered with ${provider ?? "this water provider"}`;
    if (kind === "license") return "Techs who hold it";
    return "Who can take it";
}

interface DialogProps {
    open: boolean;
    assessment: AssignmentEligibility | null;
    /** Offered only when the caller can actually do the pairing. */
    onPair?: (candidateName: string, assessment: AssignmentEligibility) => void;
    onCancel: () => void;
    onProceed: (reason: string) => void;
}

export function AssignmentWarningDialog({ open, assessment, onPair, onCancel, onProceed }: DialogProps) {
    const [reason, setReason] = useState("");
    if (!assessment) return null;

    const first = assessment.techName.split(" ")[0] ?? assessment.techName;
    const pairCandidate = assessment.reasons.find((r) => r.kind === "jurisdiction")?.whoCan[0];

    return (
        <Dialog open={open} onClose={onCancel} title="Before you assign this" className="max-w-md">
            <div className="space-y-4">
                {assessment.reasons.map((r) => (
                    <div key={r.kind} className="space-y-2">
                        <p className="text-sm">{r.sentence}</p>
                        {r.detail && (
                            <div className="rounded-md border border-amber-300 bg-amber-50 px-3 py-2 text-xs text-amber-800 dark:border-amber-700/60 dark:bg-amber-950/40 dark:text-amber-200">
                                {r.detail}
                            </div>
                        )}
                        {r.whoCan.length > 0 && (
                            <div className="rounded-md border border-border bg-muted/40 px-3 py-2 text-sm">
                                <div className="text-[11px] font-semibold uppercase tracking-wide text-foreground/70">
                                    {whoLabel(r.kind, assessment.waterProviderName)}
                                </div>
                                <div className="mt-1">{r.whoCan.join(" · ")}</div>
                            </div>
                        )}
                    </div>
                ))}

                <div className="space-y-1.5">
                    <label className="text-xs font-medium" htmlFor="assign-reason">
                        If you assign {first} anyway, say why
                    </label>
                    <input
                        id="assign-reason"
                        className="flex h-9 w-full rounded-md border border-input bg-background px-3 text-sm"
                        placeholder="e.g. Chris rides along as lead"
                        value={reason}
                        onChange={(e) => setReason(e.target.value)}
                    />
                    <p className="text-[11px] text-foreground/60">
                        This goes on the job for office review, the same as today.
                    </p>
                </div>

                <div className="flex flex-wrap justify-end gap-2">
                    <Button variant="outline" onClick={onCancel}>
                        Cancel
                    </Button>
                    {assessment.reasons.map(
                        (r) =>
                            r.fixHref && (
                                <Button key={r.kind} variant="outline" asChild>
                                    <a href={r.fixHref}>{r.fixLabel ?? "Fix it"}</a>
                                </Button>
                            ),
                    )}
                    {onPair && pairCandidate && (
                        <Button variant="outline" onClick={() => onPair(pairCandidate, assessment)}>
                            Pair with {pairCandidate.split(" ")[0]} instead
                        </Button>
                    )}
                    <Button onClick={() => onProceed(reason.trim())}>Assign {first} anyway</Button>
                </div>
            </div>
        </Dialog>
    );
}

/**
 * `const { askBeforeAssign, assignmentDialog } = useAssignmentGuard()`.
 *
 * Render `assignmentDialog` once, then `await askBeforeAssign(...)` before any
 * assignment. It resolves `{ proceed: true }` with no dialog when the pairing
 * is clean, and also when the check itself fails — a preview that cannot load
 * must never be the reason a dispatcher cannot work.
 */
export function useAssignmentGuard(opts?: {
    onPair?: (candidateName: string, assessment: AssignmentEligibility) => void;
}) {
    const [pending, setPending] = useState<AssignmentEligibility | null>(null);
    const resolver = useRef<((d: AssignmentDecision) => void) | null>(null);

    const settle = useCallback((decision: AssignmentDecision) => {
        const resolve = resolver.current;
        resolver.current = null;
        setPending(null);
        resolve?.(decision);
    }, []);

    const askBeforeAssign = useCallback(async (jobId: string, techId: string): Promise<AssignmentDecision> => {
        let assessment: AssignmentEligibility;
        try {
            assessment = await api.get<AssignmentEligibility>(
                `/api/jobs/${jobId}/assignment-eligibility?techId=${techId}`,
            );
        } catch {
            // Fail OPEN. The office-review flag still opens on the way out, so
            // the record survives even when the warning could not be fetched;
            // failing closed would make a flaky read look like a rule.
            return { proceed: true };
        }
        if (assessment.eligible) return { proceed: true };
        return new Promise<AssignmentDecision>((resolve) => {
            resolver.current = resolve;
            setPending(assessment);
        });
    }, []);

    const assignmentDialog = (
        <AssignmentWarningDialog
            open={pending !== null}
            assessment={pending}
            onPair={
                opts?.onPair
                    ? (name, assessment) => {
                          opts.onPair?.(name, assessment);
                          settle({ proceed: false, pairWith: name });
                      }
                    : undefined
            }
            onCancel={() => settle({ proceed: false })}
            onProceed={(reason) => settle({ proceed: true, reason })}
        />
    );

    return { askBeforeAssign, assignmentDialog };
}
